import { useState } from "react";
import { trpc } from "../../trpc";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Alert } from "@/components/ui/alert";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";

type ExternalAgentWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export const ExternalAgentWizard = ({ isOpen, onClose, onSuccess }: ExternalAgentWizardProps) => {
  const [merchantId, setMerchantId] = useState("");
  const [agentName, setAgentName] = useState("");
  const [topic, setTopic] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");

  const merchantsQuery = trpc.merchant.merchants.useQuery();
  const { data: merchants, isLoading: merchantsLoading, error: merchantsError } = merchantsQuery;

  const createMutation = trpc.agent.createExternalAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente externo creado exitosamente");
      handleClose();
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al crear el agente externo");
    },
  });

  const handleClose = () => {
    setMerchantId("");
    setAgentName("");
    setTopic("");
    setWebhookUrl("");
    onClose();
  };

  const handleSubmit = () => {
    createMutation.mutate({
      merchantId,
      agentName,
      topic,
      webhookUrl,
    });
  };

  const isValid = merchantId && agentName.trim() && topic.trim() && webhookUrl.trim();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Crear Agente Externo</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {merchantsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner className="h-6 w-6" />
            </div>
          ) : merchantsError ? (
            <Alert status="error">Error loading merchants: {merchantsError.message}</Alert>
          ) : (
            <div className="flex flex-col gap-2">
              <Label htmlFor="extMerchant">Merchant *</Label>
              <SearchableSelect
                id="extMerchant"
                value={merchantId}
                onValueChange={setMerchantId}
                placeholder="Selecciona un merchant..."
                searchPlaceholder="Buscar merchant..."
                items={
                  merchants?.map((m) => ({
                    value: m.id,
                    label: `${m.id} (${m.type})`,
                  })) || []
                }
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="extAgentName">Nombre del agente *</Label>
            <Input
              id="extAgentName"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="Ej: Lambda Analytics"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="extTopic">Topic *</Label>
            <Input
              id="extTopic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ej: lambda-analytics"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="extWebhookUrl">Webhook URL *</Label>
            <Input
              id="extWebhookUrl"
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={handleClose}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={!isValid || createMutation.isLoading}>
              {createMutation.isLoading ? "Creando..." : "Crear"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
